import { BentoCard, BentoCardContent } from '@/app/_components/core/bento-card';
import { RespImage } from '@/app/_components/core/resp-image';
import { RichText, type RichTextClassConfig } from '@/app/_components/core/rich-text';
import { SmartLink } from '@/app/_components/core/smart-link';
import { CardCarouselElement } from '@/app/_components/shared/card-carousel/card-carousel.types';
import { richTextHasInlineLinks } from '@/app/_components/shared/utils/component-helpers';
import { cn } from '@/app/_lib/utils';

const imageCardRichText: RichTextClassConfig = {
  copytext: 'text-base font-normal leading-relaxed text-muted-foreground',
  list: 'text-base font-normal leading-relaxed text-muted-foreground',
  link: 'text-muted-foreground',
};

interface ImageCardProps {
  card: CardCarouselElement;
  className?: string;
  priority?: boolean;
  trackId?: string;
}

export function ImageCard({ card, className, priority, trackId }: ImageCardProps) {
  // Don't wrap card in link if text contains inline links (nested links = invalid HTML)
  const hasInlineLinks = richTextHasInlineLinks(card.text);
  const isLinked = card.cta?.linkUrl && !hasInlineLinks;
  // Only render the content block when there's a title or text — an image-only
  // card shows just the image, without an empty padding block below it.
  const hasTextContent = Boolean(card.title) || Boolean(card.text);

  const CardWrapper = (
    <BentoCard as="article" className={cn('group h-full', className)}>
      {card.image?.srcsetCandidates &&
      Array.isArray(card.image.srcsetCandidates) ? (
        <div className="relative aspect-square w-full overflow-hidden bg-muted">
          <RespImage
            srcsetCandidates={card.image.srcsetCandidates}
            alt={card.image.altText ?? card.title ?? ''}
            title={card.image.title ?? undefined}
            sizes="(min-width: 1920px) 469px, (min-width: 1280px) 384px, (min-width: 1024px) 315px, (min-width: 768px) 356px, (min-width: 640px) 608px, calc(100vw - 32px)"
            priority={priority}
            className={cn(
              'size-full  object-cover transition-transform duration-300',
              isLinked && '[@media(hover:hover)]:group-hover:scale-105'
            )}
          />
        </div>
      ) : null}

      {hasTextContent && (
        <BentoCardContent className="flex grow flex-col gap-6 p-6">
          {card.title && (
            <h3 className="text-center text-lg font-medium uppercase text-primary">
              {card.title}
            </h3>
          )}

          {card.text && (
            <RichText classConfig={imageCardRichText}>{card.text}</RichText>
          )}
        </BentoCardContent>
      )}
    </BentoCard>
  );

  if (card.cta?.linkUrl && !hasInlineLinks) {
    return (
      <SmartLink
        href={card.cta.linkUrl}
        target={card.cta.linkTarget}
        className="block h-full p-0.5"
        data-track-id={trackId}
      >
        {CardWrapper}
      </SmartLink>
    );
  }

  return CardWrapper;
}
